import { useEffect, useRef, useState } from 'react';
import { analyzePart } from './analyzePart';
import { PartAnalysis } from './types';

export interface UsePartAnalysis {
  analyzing: boolean;
  error: string | null;
  /** Null until the current `meshRef` has been measured (and whenever there is
   *  no mesh). */
  analysis: PartAnalysis | null;
}

/**
 * Measure `meshRef` whenever it changes, exposing `{ analyzing, error, analysis }`.
 * Re-measuring the same ref is skipped; clearing the ref ('') resets to idle. A
 * ref that changes mid-flight cancels the stale result (last-write-wins), so a
 * quick re-upload can't paint the previous part's measurements.
 *
 * Generalized from the lattice converter's inline measure effect (#315) so any
 * feature can measure an uploaded part with one hook.
 */
export function usePartAnalysis(meshRef: string): UsePartAnalysis {
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<PartAnalysis | null>(null);
  // The ref we've already kicked off a measure for — guards against re-running
  // on unrelated re-renders. NOT reset on cancel, so A→B→A re-measures (the B
  // assignment moved it off 'A'); reset to '' only when the mesh is cleared.
  const analyzedRef = useRef<string>('');

  useEffect(() => {
    if (!meshRef) {
      setAnalysis(null);
      setError(null);
      setAnalyzing(false);
      analyzedRef.current = '';
      return;
    }
    if (analyzedRef.current === meshRef) return;
    analyzedRef.current = meshRef;
    let cancelled = false;
    setAnalyzing(true);
    setError(null);
    setAnalysis(null);
    (async () => {
      try {
        const result = await analyzePart(meshRef);
        if (!cancelled) setAnalysis(result);
      } catch (err) {
        if (!cancelled)
          setError(
            err instanceof Error ? err.message : 'Could not measure the part.',
          );
      } finally {
        if (!cancelled) setAnalyzing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [meshRef]);

  return { analyzing, error, analysis };
}
